import { NgClass } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';

import { CadsIconViewBitComponent } from '../../bits/cads-icon-view-bit/cads-icon-view-bit.component';
import type { CadsIconViewBitConfig } from '../../bits/cads-icon-view-bit/cads-icon-view-bit.config';
import { lockBodyScroll, forceUnlockBodyScroll } from '../../modal/cads-body-scroll-lock';
import { CadsMainTemplateComponent } from '../cads-main-template/cads-main-template.component';
import { CADS_MAIN_TEMPLATE_MODAL_CONTEXT } from '../cads-main-template/cads-main-template-modal.context';

import type { CadsMainTemplateModalConfig, CadsMainTemplateModalSize } from './cads-main-template-modal.config';

/**
 * Modal shell with a built-in {@link CadsMainTemplateComponent}: full-viewport scrim, centered panel on wide viewports,
 * and a bottom-affixed sheet below {@link CadsMainTemplateModalConfig.mobileMaxWidthPx} (legacy `bw-modal-legacy`
 * parity). Pass {@link CadsMainTemplateModalConfig.template} and project main-template slots; built-in **back** is
 * always suppressed. Dismiss via scrim, Escape, optional close, or footer CTAs (`(dismissed)` / `(secondaryTapped)`).
 */
@Component({
  selector: 'cads-main-template-modal',
  imports: [CadsIconViewBitComponent, CadsMainTemplateComponent, NgClass],
  templateUrl: './cads-main-template-modal.component.html',
  styleUrl: './cads-main-template-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: CADS_MAIN_TEMPLATE_MODAL_CONTEXT, useValue: true }],
  host: {
    class: 'cads-main-template-modal-host',
  },
})
export class CadsMainTemplateModalComponent {
  readonly config = input.required<CadsMainTemplateModalConfig>();

  /** Fires when the user dismisses via scrim, Escape, or the optional close control. */
  readonly dismissed = output<void>();
  /** Forwarded from the inner {@link CadsMainTemplateComponent}. */
  readonly secondaryTapped = output<void>();
  /** Forwarded from the inner {@link CadsMainTemplateComponent}. */
  readonly primaryTapped = output<void>();

  private readonly _injector = inject(Injector);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _dialogPanel = viewChild<ElementRef<HTMLElement>>('dialogPanel');
  private readonly _mobileSheet = signal(false);
  private _bodyScrollUnlock: (() => void) | null = null;

  readonly templateConfig = computed(() => this.config().template);

  readonly sizeClass = computed((): `cads-main-template-modal__panel--${CadsMainTemplateModalSize}` => {
    const size = this.config().size ?? 'medium';
    return `cads-main-template-modal__panel--${size}`;
  });

  readonly showCloseButton = computed(() => this.config().showCloseButton === true);

  readonly accessibleLabel = computed(() => {
    const fromConfig = this.config().accessibleLabel?.trim();
    if (fromConfig) {
      return fromConfig;
    }
    const title = this.config().template.heading?.title?.trim();
    if (title) {
      return title;
    }
    if (this.showCloseButton()) {
      return 'Close dialog';
    }
    return null;
  });

  readonly mobileMaxWidthPx = computed(() => {
    const raw = this.config().mobileMaxWidthPx;
    if (raw != null && Number.isFinite(raw) && raw > 0) {
      return raw;
    }
    return 720;
  });

  readonly sheetClass = computed(() => this._mobileSheet());

  /** When {@link CadsMainTemplateModalConfig.panelHeightPx} is set, stretch main-template body to fill the panel. */
  readonly panelFixedHeight = computed(() => this.panelStyles() != null);

  /** Inline height when {@link CadsMainTemplateModalConfig.panelHeightPx} is set (iframe / fixed-body modals). */
  readonly panelStyles = computed((): Record<string, string> | null => {
    const px = this.config().panelHeightPx;
    if (px == null || !Number.isFinite(px) || px <= 0) {
      return null;
    }
    const viewportCap = this._mobileSheet() ? '92dvh' : '80dvh';
    return {
      height: `${px}px`,
      'max-height': `min(${px}px, ${viewportCap})`,
    };
  });

  readonly closeIconConfig: CadsIconViewBitConfig = {
    icon: 'close',
    shape: 'icon',
    size: 'medium',
    collapsed: true,
    iconSizePx: 24,
    color: 'var(--cads-color-mid-gray)',
  };

  constructor() {
    effect(
      () => {
        if (typeof document === 'undefined' || this.config().lockBodyScroll === false) {
          return;
        }
        this._bodyScrollUnlock = lockBodyScroll();
        return () => {
          this._releaseBodyScrollLock();
        };
      },
      { injector: this._injector },
    );

    this._destroyRef.onDestroy(() => {
      this._releaseBodyScrollLock();
      forceUnlockBodyScroll();
    });

    afterNextRender(
      () => {
        this._dialogPanel()?.nativeElement.focus();
      },
      { injector: this._injector },
    );

    effect(
      () => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
          return;
        }
        const maxPx = this.mobileMaxWidthPx();
        const mql = window.matchMedia(`(max-width: ${maxPx}px)`);
        const sync = (): void => {
          this._mobileSheet.set(mql.matches);
        };
        sync();
        mql.addEventListener('change', sync);
        return () => {
          mql.removeEventListener('change', sync);
        };
      },
      { injector: this._injector, allowSignalWrites: true },
    );
  }

  protected _onBackdropClick(event: MouseEvent): void {
    if (this.config().closeOnBackdropClick === false) {
      return;
    }
    if (event.target === event.currentTarget) {
      this._dismiss();
    }
  }

  protected _onEscape(event: Event): void {
    if (this.config().closeOnEscape === false) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this._dismiss();
  }

  protected _onCloseClick(): void {
    this._dismiss();
  }

  protected _onSecondaryTap(): void {
    this.secondaryTapped.emit();
  }

  protected _onPrimaryTap(): void {
    this.primaryTapped.emit();
  }

  private _dismiss(): void {
    this.dismissed.emit();
  }

  private _releaseBodyScrollLock(): void {
    if (this._bodyScrollUnlock) {
      this._bodyScrollUnlock();
      this._bodyScrollUnlock = null;
    }
  }
}
